import React from "react";
import { Outlet, Link, useLocation, Navigate } from "react-router-dom";
import { Calendar, Users, BarChart3, Mail, ChefHat, LogOut, FileText, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

export const Layout = () => {
  const location = useLocation();
  const { user, logout, hasAccess } = useAuth();

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/login" />;
  }

  const allNavItems = [
    { path: "/", label: "The Host Stand", icon: Calendar, page: "dashboard" },
    { path: "/crm", label: "Client CRM", icon: Users, page: "crm" },
    { path: "/analytics", label: "Analytics & ROI", icon: BarChart3, page: "analytics" },
    { path: "/marketing", label: "Email Campaigns", icon: Mail, page: "marketing" },
    { path: "/logs", label: "System Logs", icon: FileText, page: "logs" },
    { path: "/staff", label: "Manage Staff", icon: Shield, page: "staff" },
  ];

  // Filter navigation based on user role
  const navItems = allNavItems.filter(item => hasAccess(item.page));

  return (
    <div className="flex h-screen bg-[#09090B] text-[#FAFAFA]">
      <aside className="w-64 border-r border-[#27272A] bg-[#18181B]/50 backdrop-blur-xl flex flex-col">
        <div className="p-6 border-b border-[#27272A]">
          <div className="flex items-center gap-3">
            <ChefHat className="w-8 h-8 text-[#22C55E]" />
            <div>
              <h1 className="text-lg font-bold tracking-tight font-mono">Buffet OS</h1>
              <p className="text-xs text-[#A1A1AA]">des Continents</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <li key={item.path}>
                  <Link
                    to={item.path}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className={`sidebar-link flex items-center gap-3 ${
                      isActive ? "active" : ""
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm font-medium">{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="p-4 border-t border-[#27272A] space-y-3">
          {/* User Info */}
          <div className="glass-card p-3 rounded-lg">
            <p className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-1">Logged in as</p>
            <p className="text-sm font-semibold text-[#FAFAFA]">{user.name}</p>
            <p className="text-xs text-[#A1A1AA] font-mono">{user.role.replace('_', ' ').toUpperCase()}</p>
          </div>

          {/* Logout Button */}
          <Button
            data-testid="logout-button"
            onClick={logout}
            variant="outline"
            className="w-full border-[#27272A] hover:bg-[#EF4444]/10 hover:border-[#EF4444] hover:text-[#EF4444] transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Log Out
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto scroll-container">
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;